import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Text,
  Spinner,
  Flex,
  Avatar,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { clientsApi, bookingsApi, mastersApi, servicesApi, getStoredToken } from 'api';
import { useAuth } from 'contexts/AuthContext';
import { isInpatientBooking } from 'utils/bookingCareTypeSort';
import { visitCareBadge } from 'utils/visitHistory';

const STATUS_LABELS = {
  booked: 'Записан',
  confirmed: 'Подтверждён',
  completed: 'Завершён',
  cancelled: 'Отменён',
  no_show: 'Не пришёл',
};

function formatBookingWhen(dateStr, timeStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  const datePart = d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  if (!timeStr) return datePart;
  const t = String(timeStr).slice(0, 5);
  return `${datePart}, ${t}`;
}

export function SearchBar(props) {
  const { variant, background, children, placeholder, borderRadius, ...rest } = props;
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [catalogReady, setCatalogReady] = useState(false);
  const mastersCacheRef = useRef([]);
  const servicesCacheRef = useRef([]);
  const wrapRef = useRef(null);

  const searchIconColor = useColorModeValue('gray.700', 'white');
  const inputBg = useColorModeValue('secondaryGray.300', 'navy.900');
  const inputText = useColorModeValue('gray.800', 'white');
  const panelBg = useColorModeValue('white', 'navy.800');
  const borderCol = useColorModeValue('gray.200', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'navy.600');
  const placeholderColor = useColorModeValue('gray.500', 'whiteAlpha.600');

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !getStoredToken()) return;
    if (catalogReady) return;

    Promise.all([mastersApi.list(), servicesApi.list()])
      .then(([mastersList, servList]) => {
        mastersCacheRef.current = Array.isArray(mastersList) ? mastersList : [];
        servicesCacheRef.current = Array.isArray(servList) ? servList : [];
        setCatalogReady(true);
      })
      .catch(() => {
        mastersCacheRef.current = mastersCacheRef.current || [];
        servicesCacheRef.current = servicesCacheRef.current || [];
        setCatalogReady(true);
      });
  }, [isAuthenticated, catalogReady]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setClients([]);
      setBookings([]);
      setDoctors([]);
      setServices([]);
      setLoading(false);
      return;
    }
    if (!isAuthenticated || !getStoredToken()) {
      return;
    }

    setLoading(true);
    const timer = setTimeout(() => {
      const ql = q.toLowerCase();
      const cachedMasters = mastersCacheRef.current || [];
      const cachedServices = servicesCacheRef.current || [];
      const qd = cachedMasters
        .filter((m) => (m.name || '').toLowerCase().includes(ql))
        .slice(0, 6);
      const qs = cachedServices
        .filter((s) => (s.name || '').toLowerCase().includes(ql))
        .slice(0, 6);
      setDoctors(qd);
      setServices(qs);

      Promise.all([
        clientsApi.list({ search: q, per_page: 8 }),
        bookingsApi.list({ search: q }),
      ])
        .then(([cl, bk]) => {
          const qc = Array.isArray(cl?.data) ? cl.data.slice(0, 8) : [];
          const qb = Array.isArray(bk) ? bk.slice(0, 8) : [];
          setClients(qc);
          setBookings(qb);
        })
        .catch(() => {
          setClients([]);
          setBookings([]);
        })
        .finally(() => setLoading(false));
    }, 320);

    return () => clearTimeout(timer);
  }, [query, isAuthenticated, catalogReady]);

  const catalogLoading = query.trim().length >= 2 && !catalogReady;

  const total = clients.length + bookings.length + doctors.length + services.length;
  const showDropdown = open && query.trim().length >= 2;

  const resetAndClose = () => {
    setOpen(false);
    setQuery('');
  };

  const goClient = (id) => {
    resetAndClose();
    navigate(`/admin/medical-records/${id}`);
  };

  const goMedical = (id) => {
    resetAndClose();
    navigate(`/admin/medical-records/${id}?from=ambulatory`);
  };

  const goBookingMedical = (b) => {
    const clientId = b.clientId ?? b.client_id;
    if (!clientId) {
      goBooking(b);
      return;
    }
    resetAndClose();
    const from = isInpatientBooking(b) ? 'inpatient' : 'ambulatory';
    navigate(`/admin/medical-records/${clientId}?bookingId=${b.id}&from=${from}`);
  };

  const goBooking = (b) => {
    resetAndClose();
    navigate(`/admin/reception?tab=schedule&date=${encodeURIComponent(b.date)}`);
  };

  const goMasters = () => {
    resetAndClose();
    navigate('/admin/masters');
  };

  const goServices = () => {
    resetAndClose();
    navigate('/admin/masters?tab=services');
  };

  return (
    <Box ref={wrapRef} position="relative" w={{ base: '100%', md: 'min(320px, 36vw)' }} {...rest}>
      <InputGroup>
        <InputLeftElement pointerEvents="none" h="100%">
          <IconButton
            bg="inherit"
            borderRadius="inherit"
            _hover="none"
            _active={{ bg: 'inherit', transform: 'none', borderColor: 'transparent' }}
            _focus={{ boxShadow: 'none' }}
            icon={<SearchIcon color={searchIconColor} w="15px" h="15px" />}
            aria-hidden
            tabIndex={-1}
          />
        </InputLeftElement>
        <Input
          variant="search"
          fontSize="sm"
          bg={background || inputBg}
          color={inputText}
          fontWeight="500"
          pl="40px"
          _placeholder={{ color: placeholderColor, fontSize: '14px' }}
          borderRadius={borderRadius || '30px'}
          placeholder={placeholder || 'Поиск: фамилия, телефон, врач, услуга…'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </InputGroup>

      {showDropdown && (
        <Box
          position="absolute"
          left={0}
          right={0}
          top="calc(100% + 6px)"
          zIndex={2000}
          bg={panelBg}
          borderWidth="1px"
          borderColor={borderCol}
          borderRadius="lg"
          boxShadow="lg"
          maxH="min(420px, 70vh)"
          overflowY="auto"
          py={2}
        >
          {loading || catalogLoading ? (
            <Flex justify="center" py={6}>
              <Spinner size="sm" />
            </Flex>
          ) : total === 0 ? (
            <Text fontSize="sm" color={muted} px={4} py={3}>
              Ничего не найдено. Попробуйте фамилию, телефон или название услуги.
            </Text>
          ) : (
            <>
              {clients.length > 0 && (
                <Box px={2} mb={2}>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color={muted}
                    px={2}
                    mb={1}
                    textTransform="uppercase"
                  >
                    Пациенты
                  </Text>
                  {clients.map((c) => (
                    <Flex
                      key={`c-${c.id}`}
                      align="center"
                      gap={3}
                      px={2}
                      py={2}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: hoverBg }}
                      onClick={() => goClient(c.id)}
                    >
                      <Avatar
                        size="sm"
                        name={c.fullName || c.full_name}
                        src={c.photoUrl || c.photo_url || undefined}
                      />
                      <Box flex={1} minW={0}>
                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                          {c.fullName || c.full_name}
                        </Text>
                        <Text fontSize="xs" color={muted} noOfLines={1}>
                          {c.phone}
                        </Text>
                      </Box>
                      <Text
                        as="button"
                        type="button"
                        fontSize="xs"
                        color="brand.500"
                        fontWeight="600"
                        flexShrink={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          goMedical(c.id);
                        }}
                      >
                        Медкнижка
                      </Text>
                    </Flex>
                  ))}
                </Box>
              )}

              {bookings.length > 0 && (
                <>
                  {clients.length > 0 && <Divider my={1} />}
                  <Box px={2} mb={2}>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color={muted}
                      px={2}
                      mb={1}
                      textTransform="uppercase"
                    >
                      Записи (приёмы)
                    </Text>
                    {bookings.map((b) => {
                      const careLabel = visitCareBadge(b.careType ?? b.care_type, b)?.label;
                      return (
                      <Flex
                        key={`b-${b.id}`}
                        align="flex-start"
                        gap={2}
                        px={2}
                        py={2}
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={() => goBookingMedical(b)}
                      >
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                            {b.clientName || 'Пациент'}
                          </Text>
                          <Text fontSize="xs" color={muted} noOfLines={1}>
                            {b.serviceName || 'Услуга'} · {b.doctorName || 'Врач'}
                          </Text>
                          <Text fontSize="xs" color={muted} mt={0.5}>
                            {formatBookingWhen(b.date, b.time)}{' '}
                            {careLabel ? (
                              <Badge ml={1} fontSize="0.65rem" colorScheme="purple">
                                {careLabel}
                              </Badge>
                            ) : null}
                            <Badge ml={1} fontSize="0.65rem" colorScheme="blue">
                              {STATUS_LABELS[b.status] || b.status}
                            </Badge>
                          </Text>
                        </Box>
                      </Flex>
                    );
                    })}
                  </Box>
                </>
              )}

              {doctors.length > 0 && (
                <>
                  {(clients.length > 0 || bookings.length > 0) && <Divider my={1} />}
                  <Box px={2} mb={2}>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color={muted}
                      px={2}
                      mb={1}
                      textTransform="uppercase"
                    >
                      Врачи
                    </Text>
                    {doctors.map((m) => (
                      <Flex
                        key={`d-${m.id}`}
                        align="center"
                        gap={2}
                        px={2}
                        py={2}
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={goMasters}
                      >
                        <Text fontSize="sm" fontWeight="500">
                          {m.name}
                        </Text>
                      </Flex>
                    ))}
                  </Box>
                </>
              )}

              {services.length > 0 && (
                <>
                  {(clients.length > 0 || bookings.length > 0 || doctors.length > 0) && <Divider my={1} />}
                  <Box px={2}>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color={muted}
                      px={2}
                      mb={1}
                      textTransform="uppercase"
                    >
                      Услуги
                    </Text>
                    {services.map((s) => (
                      <Flex
                        key={`s-${s.id}`}
                        align="center"
                        justify="space-between"
                        gap={2}
                        px={2}
                        py={2}
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={goServices}
                      >
                        <Text fontSize="sm" noOfLines={1}>
                          {s.name}
                        </Text>
                        <Text fontSize="xs" color={muted} flexShrink={0}>
                          {s.duration} мин
                        </Text>
                      </Flex>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
